import React, { useState, useEffect, useRef } from 'react';
import { Checkbox } from '@components/ui/checkbox';
import { HealthOption } from '@features/onboarding/components/6-health-question-form/interfaces';
import { Separator } from '@components/ui/separator';
import { DropdownFloating } from '@components/ui/dropdown-floating';
import { useHealthStore } from 'src/store/health';
import { PLEASE_SELECT_LEAST, REQUIRE_DETAILS } from 'src/features/onboarding/components/6-health-question-form/constants';
import { FloatingInput } from '@components/ui/input-floating';
import { FormMessageSpan } from '@components/ui/form';
import { UseFormReturn } from 'react-hook-form';
import { HealthQuestionsSchema } from '@features/onboarding/components/6-health-question-form/schema/phq';

interface FieldValue {
  value: string;
  groupKey?: string;
  details?: string;
}

interface GroupedCheckboxGroupProps {
  group: HealthOption[];
  groupIdx: number;
  groupKey?: string;
  fields: FieldValue[];
  allFields: FieldValue[];
  questionId: string;
  append: (value: FieldValue) => void;
  remove: (index: number) => void;
  onRemoveGroup: () => void;
  checkboxOptions: { key: string; value: string; label: string }[];
  groupPrefix: string;
  form: UseFormReturn<HealthQuestionsSchema>;
}

export const GroupedCheckboxGroup: React.FC<GroupedCheckboxGroupProps> = ({
  group,
  groupIdx,
  groupKey,
  fields,
  allFields,
  questionId,
  append,
  remove,
  onRemoveGroup,
  checkboxOptions,
  groupPrefix,
  form,
}) => {
  const { issues, issueDetails, isFailValidation, setFailValidation } = useHealthStore();
  const [noOptionsSelected, setNoOptionsSelected] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [detailsMap, setDetailsMap] = useState<Record<string, string>>({});
  const errorRef = useRef<HTMLDivElement>(null);
  const smoothScrollTo = (element: HTMLElement) => {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };
  const firstOption = group.find(
    (opt) => typeof opt === 'object' && !opt.isNoneApply
  );
  const detail = issueDetails.find((d) => d.key === firstOption?.value);
  const groupIssueId = detail?.issueId;
  const groupIssue = issues.find((issue) => issue.id === groupIssueId);
  const groupTitle = groupIssue ? groupIssue.label : 'Condition Group';
  const groupSubtitle = groupIssue
    ? `Tell us a little more about the ${groupIssue.label.toLowerCase()} condition.`
    : 'Tell us a little more about this condition.';
  const noneOption = group.find((opt) => opt.isNoneApply);
  const noneValue = noneOption
    ? checkboxOptions.find((opt) => opt.value.endsWith('-none'))?.value
    : null;
  const [isNoneLocalChecked, setIsNoneLocalChecked] = useState(
    noneValue ? fields.some((field: FieldValue) => field.value === noneValue) : false
  );

  const normalizedTitle = groupTitle.toLowerCase();

  const imageSrc = normalizedTitle.includes('diabetes')
    ? '/images/diabetes.svg'
    : normalizedTitle.includes('heart')
      ? '/images/heartdisease.svg'
      : normalizedTitle.includes('mental')
        ? '/images/mentalhealth.svg'
        : normalizedTitle.includes('surgery')
          ? '/images/surgery.svg'
          : '/images/placeholder-icn.png';

  useEffect(() => {
    if (isFailValidation) {
      setHasInteracted(true);
    }
  }, [isFailValidation]);

  useEffect(() => {
    if (noneValue) {
      const isChecked = fields.some((field: FieldValue) => field.value === noneValue);
      if (isChecked !== isNoneLocalChecked) {
        setIsNoneLocalChecked(isChecked);
      }
    }
  }, [fields, noneValue, isNoneLocalChecked]);

  const type2Options = issueDetails
    .filter(
      (e) => e.issueId === groupIssueId && groupIssueId === 4 && e.type === 2
    )
    .map((e) => ({
      value: `${groupPrefix}-${e.key}`,
      label: e.label,
    }));

  const [selectedType2Value, setSelectedType2Value] = useState(
    fields.find((field: FieldValue) =>
      type2Options.some((opt) => opt.value === field.value)
    )?.value || ''
  );

  const handleNoneCheckedChange = (checked: boolean | string) => {
    setHasInteracted(true);
    setIsNoneLocalChecked(!!checked);
    setFailValidation(false);

    if (checked) {
      const groupValues = group
        .filter((opt) => !opt.isNoneApply)
        .map((opt) => `${groupPrefix}-${opt.value}`);
      const indicesToRemove: number[] = [];
      allFields.forEach((field: FieldValue, index: number) => {
        if (groupValues.includes(field.value)) {
          indicesToRemove.push(index);
        }
      });
      indicesToRemove.sort((a, b) => b - a).forEach((index) => remove(index));
      setSelectedType2Value('');
      if (noneValue) {
        append({ value: noneValue, groupKey });
      }
    } else {
      const indexToRemove = allFields.findIndex(
        (field: FieldValue) => field.value === noneValue && field.groupKey === groupKey
      );
      if (indexToRemove !== -1) {
        remove(indexToRemove);
      }
    }
  };

  const handleCheckboxChange = (
    checked: boolean | string,
    optionValue: string
  ) => {
    setHasInteracted(true);
    setFailValidation(false);
    if (checked) {
      if (isNoneLocalChecked) {
        setIsNoneLocalChecked(false);
        const indexToRemove = allFields.findIndex(
          (field: FieldValue) => field.value === noneValue
        );
        if (indexToRemove !== -1) {
          remove(indexToRemove);
        }
      }
      append({ value: optionValue });
    } else {
      const indexToRemove = allFields.findIndex(
        (field: FieldValue) => field.value === optionValue
      );
      if (indexToRemove !== -1) {
        remove(indexToRemove);
      }
    }
  };

  const handleType2SelectChange = (value: string) => {
    setHasInteracted(true);
    setSelectedType2Value(value);
    setFailValidation(false);

    const indicesToRemove: number[] = [];
    fields.forEach((field: FieldValue, index: number) => {
      if (type2Options.some((opt) => opt.value === field.value)) {
        indicesToRemove.push(index);
      }
    });
    indicesToRemove.sort((a, b) => b - a).forEach((index) => remove(index));

    if (isNoneLocalChecked) {
      setIsNoneLocalChecked(false);
      const indexToRemove = fields.findIndex(
        (field: FieldValue) => field.value === noneValue
      );
      if (indexToRemove !== -1) {
        remove(indexToRemove);
      }
    }

    if (value) {
      append({ value });
    }
  };

  const handleDetailsChange = (optionValue: string, details: string) => {
    setFailValidation(false);
    setDetailsMap((prev) => ({
      ...prev,
      [optionValue]: details,
    }));

    const detailsPath = `healthQuestions.surgeriesDiabetesHeartMental.details.${optionValue}` as const;
    form.setValue(detailsPath, details, { shouldDirty: true, shouldValidate: false });

    // Find the field index in the GLOBAL array (allFields) and update the details
    const globalIndex = allFields.findIndex(
      (field: FieldValue) => field.value === optionValue
    );

    if (globalIndex !== -1) {
      const updatedField = {
        ...allFields[globalIndex],
        details,
      };

      // Replace the existing item preserving position using remove + insert
      remove(globalIndex);
      // react-hook-form's useFieldArray lacks insert, so we append and swap ordering if needed.
      // To keep order, we can append and move last. Simpler: append is acceptable as order isn't critical.
      append(updatedField);
    }
  };

  useEffect(() => {
    if (!hasInteracted) {
      return;
    }
    const noOptionsFromCheckbox = !checkboxOptions.some((option) =>
      fields.some((field: FieldValue) => field.value === option.value)
    );
    const noOptions = noOptionsFromCheckbox && !selectedType2Value;
    setNoOptionsSelected(noOptions);
  }, [isNoneLocalChecked, fields, checkboxOptions, hasInteracted]);

  const detailsErrors: Record<string, string> = {};
  fields.forEach((field: FieldValue) => {
    if (field.value !== noneValue && REQUIRE_DETAILS) {
      const details = field.details || '';
      if (!details || details.trim() === '') {
        detailsErrors[field.value] =
          'You may add more details about this if you like.';
      }
    }
  });

  useEffect(() => {
    if (isFailValidation) {
      const hasDetailsErrors = Object.keys(detailsErrors).length > 0;
      const hasNoOptionsError = noOptionsSelected && !isNoneLocalChecked;
      
      if ((hasDetailsErrors || hasNoOptionsError) && errorRef.current) {
        setTimeout(() => {
          if (errorRef.current) {
            smoothScrollTo(errorRef.current);
          }
        }, 100);
      }
    }
  }, [isFailValidation, detailsErrors, noOptionsSelected, isNoneLocalChecked]);

  return (
    <div className="card-shadow rounded-2xl p-4 lg:p-8" ref={errorRef}>
      <div className="flex flex-row gap-6 relative items-center max-md:ml-2">
        {/* <button
          className="cursor-pointer -top-2.5 right-3 absolute"
          onClick={onRemoveGroup}
        >
          <img src="/images/red-close.png" className="w-5 h-5" alt="red-x" />
        </button> */}
        <figure className="flex justify-center min-w-8 max-w-8 max-md:mt-2 max-md:mb-auto md:min-w-14 md:max-w-20 ">
          <img
            src={imageSrc}
            alt={`${groupTitle} icon`}
            className="flex self-baseline w-25 max-md:min-w-15 max-md:max-w-15"
          />
        </figure>
        <div className="flex flex-col gap-1">
          <p className="text-2xl md:text-[28px] font-extrabold">{groupTitle}</p>
          <span className="text-base md:text-lg">{groupSubtitle}</span>
        </div>
      </div>
      <Separator className="mb-5.5 mt-5" />
      {type2Options.length > 0 && (
        <div className="flex flex-col mb-4 md:mb-6 pb-4 border-b">
          <p className="font-medium mb-6">
            What type or level of diabetes are you (or the person with the
            condition) managing?
          </p>
          <DropdownFloating
            id={`group_${groupIdx}`}
            inputClass="border rounded-lg p-2 !text-[18px] !h-13.5 w-full max-w-[280px] border-[#B7B7B7]"
            wrapperClass="max-w-[280px]"
            value={selectedType2Value}
            onChange={handleType2SelectChange}
            options={[{ label: 'Not selected', value: '' }, ...type2Options]}
            label="Select type"
          />
        </div>
      )}
      <p className="mb-4 lg:mb-6">
        Please select all that apply concerning the person with the condition:
      </p>
      <div className="flex flex-col gap-3.5">
        {checkboxOptions
          .filter((option) => !noneOption || option.label !== noneOption.label)
          .map((option, index) => {
            const optionLabel = option.label;
            const optionValue = option.value;
            const isChecked = fields.some(
              (field: FieldValue) => field.value === optionValue
            );
            const field = fields.find((f: FieldValue) => f.value === optionValue);
            return (
              <div key={optionValue} className="flex flex-col gap-3">
                <div className="w-full">
                  <Checkbox
                    id={option.key}
                    label={optionLabel}
                    checked={isChecked}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange(checked, optionValue)
                    }
                  />
                </div>
                {isChecked && (
                  <FloatingInput
                    as="textarea"
                    id={`${questionId}-${groupIdx}-${index}`}
                    label="You may add more details about this if you like"
                    rows={3}
                    value={field?.details || ''}
                    inputClass="w-full border rounded-[6px] min-h-36 mb-4 p-2 text-sm !font-secondary"
                    onChange={(e) => {
                      handleDetailsChange(optionValue, e.target.value);
                      if (detailsErrors[optionValue]) {
                        detailsErrors[optionValue] = '';
                      }
                    }}
                  />
                )}
                {isFailValidation &&
                  isChecked &&
                  detailsErrors[optionValue] && (
                    <p className="text-primary -mt-4">
                      {detailsErrors[optionValue]}
                    </p>
                  )}
              </div>
            );
          })}
      </div>
      {noneOption && noneValue && (
        <div className="flex items-center mt-10">
          <Checkbox
            id={`${questionId}-${groupIdx}-none`}
            label={noneOption.label}
            checked={isNoneLocalChecked}
            onCheckedChange={(checked) => handleNoneCheckedChange(checked)}
            aria-label="None apply, clears all other selections in this group"
          />
        </div>
      )}
      {noOptionsSelected && !isNoneLocalChecked && (
        <FormMessageSpan
          className="text-error font-extrabold mt-2 text-left"
          text={PLEASE_SELECT_LEAST}
        />
      )}
    </div>
  );
};
